// GP Viva Practice — drives the topic picker and examiner chat on the Learn page.

import { updateCreditsDisplay } from './credits';

const COMPLETE_MARKER = '[[VIVA_COMPLETE]]';
const TOPIC_RETRY_DELAYS = [1000, 2000, 4000];

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : '';
}

function initViva() {
  const page = document.querySelector('.viva-page');
  const topicList = document.getElementById('viva-topic-list');
  if (!page || !topicList) return;

  const urls = page.dataset;
  const errorBanner = document.getElementById('viva-error-banner');
  const topicCard = document.getElementById('viva-topic-card');
  const topicSearch = document.getElementById('viva-topic-search');
  const topicHint = document.getElementById('viva-topic-hint');
  const startBtn = document.getElementById('viva-start-btn');
  const startLabel = document.getElementById('viva-start-label');
  const chatView = document.getElementById('viva-chat-view');
  const topicTitle = document.getElementById('viva-topic-title');
  const chatHistory = document.getElementById('viva-chat-history');
  const answerInput = document.getElementById('viva-answer-input');
  const answerLabel = document.getElementById('viva-answer-label');
  const sendBtn = document.getElementById('viva-send-btn');
  const restartBtn = document.getElementById('viva-restart-btn');

  let selectedTopicId = null;
  let selectedTopicName = null;
  let transcript = [];
  let vivaComplete = false;
  // The server's saved copy of this viva, so each turn updates the same record.
  let vivaSessionId = null;

  // The topic sits beside the GP Viva Practice wordmark rather than in a badge
  // of its own, so the chat view keeps the whole width for the transcript.
  function showTopicTitle(name) {
    topicTitle.textContent = name || '';
  }

  // A disabled button alone reads as "you can't", not "hang on": spin while the
  // examiner's opening question is being written.
  function setStartLoading(loading) {
    startBtn.classList.toggle('is-loading', loading);
    startBtn.disabled = loading;
    startBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
    startLabel.textContent = loading ? 'Starting…' : 'Start Viva';
  }

  // With a topic chosen the list folds down to that one row, so the picker
  // stops taking up the screen while the choice stays on show.
  function setListCollapsed(collapsed) {
    topicList.classList.toggle('collapsed', collapsed);
    topicHint.classList.toggle('is-hidden', !collapsed);
  }

  function showError(message) {
    errorBanner.textContent = message;
    errorBanner.classList.add('active');
  }

  function clearError() {
    errorBanner.classList.remove('active');
  }

  // A signed-out session cannot run a viva, so bounce back to the landing page
  // rather than leaving the trainee staring at a dead screen.
  function handleUnauthorised() {
    window.location.replace('/');
  }

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify(body)
    });

    if (response.status === 401) {
      handleUnauthorised();
      return null;
    }

    const data = await response.json().catch(() => ({}));
    if (response.status === 402) {
      throw new Error('You have run out of credits. Please contact an admin to request more.');
    }
    if (!response.ok) throw new Error(data.error || `Server error ${response.status}`);
    return data;
  }

  function renderTopics(topics) {
    topicList.innerHTML = '';

    if (!topics.length) {
      const empty = document.createElement('div');
      empty.className = 'topic-list-message';
      empty.textContent = 'No topics are available yet. Please check back soon.';
      topicList.appendChild(empty);
      return;
    }

    topics.forEach((topic) => {
      const label = document.createElement('label');
      label.className = 'topic-label';

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'viva-topic';
      radio.value = topic.id;

      const name = document.createElement('span');
      name.className = 'topic-name';
      name.textContent = topic.name;

      label.appendChild(radio);
      label.appendChild(name);

      // change rather than click, so arrow-key selection works too.
      radio.addEventListener('change', () => {
        topicList.querySelectorAll('.topic-label').forEach((el) => el.classList.remove('selected'));
        label.classList.add('selected');
        selectedTopicId = topic.id;
        selectedTopicName = topic.name;
        showTopicTitle(topic.name);
        startBtn.disabled = false;
      });

      // Picking a topic folds the list; clicking the one already picked opens
      // it back up. This has to sit on the radio, not the label: a click on the
      // label activates the radio, whose own click bubbles back to the label,
      // so a label listener would fire twice and undo itself. The radio sees
      // exactly one click either way. It also runs before `change`, so on a new
      // selection selectedTopicId is still the previous topic and the list
      // folds rather than reopening.
      radio.addEventListener('click', () => {
        const reopening = topicList.classList.contains('collapsed') && selectedTopicId === topic.id;
        setListCollapsed(!reopening);
      });

      topicList.appendChild(label);
    });

    // A fresh list is read from the first topic down, not from wherever the
    // browser happened to leave the box on a previous visit.
    topicList.scrollTop = 0;
  }

  async function loadTopics() {
    for (let attempt = 0; attempt <= TOPIC_RETRY_DELAYS.length; attempt += 1) {
      try {
        const response = await fetch(urls.topicsUrl, { headers: { Accept: 'application/json' } });
        if (response.status === 401) {
          handleUnauthorised();
          return;
        }
        if (!response.ok) throw new Error(`Server error ${response.status}`);
        renderTopics(await response.json());
        return;
      } catch (e) {
        if (attempt === TOPIC_RETRY_DELAYS.length) {
          topicList.innerHTML = '';
          const failed = document.createElement('div');
          failed.className = 'topic-list-message error';
          failed.textContent = 'Failed to load topics. Please refresh the page.';
          topicList.appendChild(failed);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, TOPIC_RETRY_DELAYS[attempt]));
      }
    }
  }

  // Distance from the top of the transcript's scrollable content to an element
  // inside it, independent of where the box happens to be scrolled.
  function offsetWithin(el) {
    return el.getBoundingClientRect().top - chatHistory.getBoundingClientRect().top + chatHistory.scrollTop;
  }

  // A turn from the examiner is often taller than the box, so following the
  // conversation to the bottom would land the trainee halfway through it.
  // Whenever the new message runs off the bottom, put its first line at the top
  // of the box instead; a message that already fits is left where it is.
  function scrollRowToTop(row) {
    const rowTop = offsetWithin(row);
    if (rowTop + row.offsetHeight <= chatHistory.scrollTop + chatHistory.clientHeight) return;

    // Measured against the first row rather than 0, so the box's own top
    // padding stays on show.
    const first = chatHistory.firstElementChild;
    chatHistory.scrollTop = rowTop - (first ? offsetWithin(first) : 0);
  }

  function appendMessage(role, text, typing = false) {
    const row = document.createElement('div');
    row.className = `message-row ${role === 'user' ? 'trainee' : 'examiner'}`;

    const label = document.createElement('div');
    label.className = 'message-label';
    label.textContent = role === 'user' ? 'You' : 'Trainer';

    const bubble = document.createElement('div');
    bubble.className = 'message-bubble';
    if (typing) {
      bubble.classList.add('typing');
      for (let i = 0; i < 3; i += 1) {
        const dot = document.createElement('div');
        dot.className = 'dot';
        bubble.appendChild(dot);
      }
    } else {
      bubble.textContent = text;
    }

    row.appendChild(label);
    row.appendChild(bubble);
    chatHistory.appendChild(row);

    // The trainee's own answer just needs to be in view; the examiner's reply
    // gets read from its first line.
    if (role === 'user') {
      chatHistory.scrollTop = chatHistory.scrollHeight;
    } else {
      scrollRowToTop(row);
    }
    return { row, bubble };
  }

  function setAnswerEnabled(enabled) {
    answerInput.disabled = !enabled;
    sendBtn.disabled = !enabled;
  }

  // The examiner signals the end of the viva with a marker on its closing
  // message; strip it and swap the answer box for the restart button.
  function handleExaminerContent(raw) {
    if (!raw.includes(COMPLETE_MARKER)) return raw;

    vivaComplete = true;
    answerLabel.classList.add('is-hidden');
    answerInput.classList.add('is-hidden');
    sendBtn.classList.add('is-hidden');
    restartBtn.classList.remove('is-hidden');
    return raw.replace(COMPLETE_MARKER, '').trim();
  }

  async function startViva() {
    if (!selectedTopicId) return;
    clearError();
    setStartLoading(true);

    try {
      const result = await postJson(urls.startUrl, { topic_id: selectedTopicId });
      if (!result) return;

      updateCreditsDisplay(result.credits_remaining);
      vivaSessionId = result.viva_session_id || null;
      transcript = [{ role: 'assistant', content: result.content }];
      topicCard.classList.add('is-hidden');
      chatView.classList.add('active');
      showTopicTitle(result.topic_name || selectedTopicName);
      appendMessage('assistant', handleExaminerContent(result.content));
      answerInput.focus();
    } catch (e) {
      showError(e.message);
    } finally {
      setStartLoading(false);
    }
  }

  async function sendAnswer() {
    if (vivaComplete) return;
    const text = answerInput.value.trim();
    if (!text) return;

    clearError();
    answerInput.value = '';
    setAnswerEnabled(false);

    transcript.push({ role: 'user', content: text });
    appendMessage('user', text);
    const { row: typingRow } = appendMessage('assistant', '', true);

    try {
      const result = await postJson(urls.turnUrl, {
        topic_id: selectedTopicId,
        viva_session_id: vivaSessionId,
        messages: transcript
      });
      if (!result) return;

      updateCreditsDisplay(result.credits_remaining);
      transcript.push({ role: 'assistant', content: result.content });
      const bubble = typingRow.querySelector('.message-bubble');
      bubble.classList.remove('typing');
      bubble.innerHTML = '';
      bubble.textContent = handleExaminerContent(result.content);
      // The row was measured while it still held the typing dots, so place it
      // again now it holds the answer.
      scrollRowToTop(typingRow);
    } catch (e) {
      typingRow.remove();
      transcript.pop();
      showError(e.message);
    } finally {
      if (!vivaComplete) setAnswerEnabled(true);
      answerInput.focus();
    }
  }

  startBtn.addEventListener('click', startViva);
  sendBtn.addEventListener('click', sendAnswer);
  restartBtn.addEventListener('click', () => window.location.reload());

  answerInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendAnswer();
    }
  });

  topicSearch.addEventListener('input', function filterTopics() {
    // Searching is a request to see the other topics, so unfold the list.
    setListCollapsed(false);
    const query = this.value.toLowerCase();
    topicList.querySelectorAll('.topic-label').forEach((label) => {
      const name = label.querySelector('.topic-name').textContent.toLowerCase();
      label.style.display = name.includes(query) ? '' : 'none';
    });
  });

  loadTopics();
}

document.addEventListener('turbo:load', initViva);
document.addEventListener('DOMContentLoaded', initViva);
